import { toast } from '@cencosud-cencommerce/paris-uikit';
import type { Status } from '@cencosud-cencommerce/paris-uikit/dist/atoms/Inputs/TextField/partials/TextFieldBase';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';

import type { RootState } from '@/Infrastructure/State';
import { store } from '@/Infrastructure/State';
import { setBuyNowCouponData } from '@/modules/buy-now/infrastructure/slices';
import type { Address } from '@/modules/cart/domain/Address';
import type { ICommunePayload } from '@/modules/cart/domain/AddressCommune';
import type { AddressGuest } from '@/modules/cart/domain/AddressGuest';
import type {
  AuthorizedPerson,
  Cart,
  ClubParisFields,
  GiftCardFormFields,
  InvoiceFormFields
} from '@/modules/cart/domain/Cart';
import { EDiscoutCodeState } from '@/modules/cart/domain/Cart';
import type { CartRepository } from '@/modules/cart/domain/CartRepository';
import {
  addGiftCard,
  removeGiftCard
} from '@/modules/cart/infrastructure/CartReduxRepository/GiftCardThunks';
import { httpCartDtoToCart } from '@/modules/cart/infrastructure/mappers/httpCartDtoToCart';
import { postClubParis } from '@/modules/cart/services/api/mutation/addClubParis.mutations';
import { postAddInvoice } from '@/modules/cart/services/api/mutation/addInvoice.mutations';
import { createShippingAddressForGuest } from '@/modules/cart/services/api/mutation/createShippingAddressForGuest.mutations';
import {
  addDiscountCode,
  addToSaveForLater as addProductToSaveForLater,
  createBillingAddress,
  createShippingAddress,
  createShippingAddressCommune,
  mergeGuestCart,
  removeDiscountCode,
  removeLineItem as removeLineItemFromCart,
  removeOutOfStockProduct,
  removeOutOfStockProducts,
  selectShippingMethod as selectCartShippingMethod,
  setOnBehalf as setOnBehalfOf,
  updateLineItemQuantity as updateLineItem,
  updateShippingOption as updateCartShippingOption
} from '@/modules/cart/services/api/mutation/index';
import { deleteClubParis } from '@/modules/cart/services/api/mutation/removeClubParis.mutations';
import { deleteInvoice } from '@/modules/cart/services/api/mutation/removeInvoice.mutations';
import { getActiveCart } from '@/modules/cart/services/api/queries/getCart.queries';
import type { TErrorResponse } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import {
  setCouponData,
  setPaymentError
} from '@/modules/perseo-billing/store/slices/billing';
import type { IErrorHandlerBilling } from '@/modules/perseo-billing/utils/error-handler';
import {
  ECta,
  errorMessage46,
  errorTitleIngresaGiftcardNuevamente,
  Type
} from '@/modules/perseo-billing/utils/error-handler/error-handler.constants';
import { type IdentificationDocumentType } from '@/modules/shared/types/IdentificationDocumentType';
import type { shippingMethodBodyRequest } from '@/modules/shippingMethod/domain/ShippingMethodRepository';
import type {
  HttpShippingMethodDraft,
  HttpShippingOptionDraft
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { getShippingMethods } from '@/modules/shippingMethod/infrastructure/ShippingMethodsReduxRepository/ShippingMethodsReduxRepository';

import type { HttpCartDto } from '../dto/HttpCartRepositoryDto';

const CART_DISCOUNT_CODE = 'cart/discountCode';
const CART_REMOVE_DISCOUNT_CODE = 'cart/removeDiscountCode';
const SAVE_ADDRESS = 'cart/shippingAddress';
const SAVE_ADDRESS_GUEST = 'cart/shippingAddressGuest';
const SAVE_COMMUNE = 'cart/shippingAddress/commune';
const SAVE_BILLING_ADDRESS_GUEST = 'cart/billingAddressGuest';
const MERGE_CART = 'cart/mergeCart';
const UPDATE_LINE_ITEM_QUANTITY = 'cart/updateLineItemQuantity';
const REMOVE_LINE_ITEM = 'cart/removeLineItem';
const UPDATE_SHIPPING_METHOD = 'cart/updateShippingMethod';
const UPDATE_SHIPPING_OPTION = 'cart/updateShippingOption';
const ADD_SAVE_FOR_LATER = 'cart/addSaveForLater';
const SET_ON_BEHALF = 'cart/onBehalf';
const ADD_INVOICE = 'cart/addInvoice';
const REMOVE_INVOICE = 'cart/removeInvoice';
const ADD_CLUB_PARIS = 'cart/addClubParis';
const REMOVE_CLUB_PARIS = 'cart/removeClubParis';
const REMOVE_OUT_OF_STOCK_ITEM = 'cart/removeOutOfStockItem';
const REMOVE_OUT_OF_STOCK_ITEMS = 'cart/removeOutOfStockItems';

const FETCH_CART = 'cart';

export const saveShippingAddress = createAsyncThunk(
  SAVE_ADDRESS,
  async (addressPayload: Address | Omit<Address, 'id'>) => {
    const cart = await createShippingAddress(addressPayload);

    return cart && cart.data ? httpCartDtoToCart(cart.data) : undefined;
  }
);

export const saveShippingAddressCommune = createAsyncThunk(
  SAVE_COMMUNE,
  async (addressPayload: ICommunePayload) => {
    const cart = await createShippingAddressCommune(addressPayload);
    return cart && cart.data ? httpCartDtoToCart(cart.data) : undefined;
  }
);

export const saveShippingAddressForGuest = createAsyncThunk(
  SAVE_ADDRESS_GUEST,
  async (addressPayload: AddressGuest | Omit<AddressGuest, 'id'>) => {
    const cart = await createShippingAddressForGuest(addressPayload);

    return cart && cart.data ? httpCartDtoToCart(cart.data) : undefined;
  }
);

export const saveBillingAddress = createAsyncThunk(
  SAVE_BILLING_ADDRESS_GUEST,
  async (addressPayload: InvoiceFormFields) => {
    const cart = await createBillingAddress(addressPayload);

    return cart && cart.data ? httpCartDtoToCart(cart.data) : undefined;
  }
);

export const mergeCart = createAsyncThunk(
  MERGE_CART,
  async (anonymousId: string) => {
    const cart = await mergeGuestCart(anonymousId);

    return httpCartDtoToCart(cart.data);
  }
);

interface ApiError {
  error?: {
    type?: string;
    message?: string;
    code?: number;
  };
}
export const addCartDiscount = createAsyncThunk(
  CART_DISCOUNT_CODE,
  async (
    {
      discountCode,
      mode,
      sendErrorCouponGtm,
      sendInvalidCouponGtm
    }: {
      discountCode: string;
      mode: 'billing' | 'buyNow';
      sendErrorCouponGtm?: (coupon: string) => void;
      sendInvalidCouponGtm?: (coupon: string) => void;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const cart = await addDiscountCode(discountCode);

      const invalidCoupon = cart.data?.discountCodes.find(
        (coupon) => coupon.state === EDiscoutCodeState.DoesNotMatchCart
      );

      if (invalidCoupon) {
        const couponId = invalidCoupon.discountCode.id;
        const previousCart = await removeDiscountCode(couponId);

        toast({
          title: 'El cupón no aplica para tu carro',
          body: 'Este descuento aplica solo a algunos productos. Revisa sus requisitos.',
          position: 'bottom-left',
          status: 'error'
        });

        if (mode === 'billing' && sendInvalidCouponGtm) {
          sendInvalidCouponGtm(discountCode);
        }

        return httpCartDtoToCart(previousCart.data);
      }

      const hasGiftCardPayment = cart?.data?.payments?.some(
        (payment) => payment.paymentMethodInfo?.method === 'giftCard'
      );

      if (hasGiftCardPayment) {
        const updatedCart = await dispatch(removeGiftCard()).unwrap();
        const billingError: IErrorHandlerBilling = {
          contentAssets: 'BillingCardExpired',
          title: errorTitleIngresaGiftcardNuevamente,
          message: errorMessage46,
          type: Type.INFO,
          cta: ECta.ENTIENDO
        };
        dispatch(setPaymentError(billingError));

        cart.data = updatedCart as unknown as HttpCartDto;
      }

      toast({
        title: '¡Listo! Agregaste un cupón',
        body: 'Solo se puede usar uno por compra.',
        position: 'bottom-left',
        status: 'success'
      });

      return cart && cart.data ? httpCartDtoToCart(cart.data) : undefined;
    } catch (error) {
      const errorTyped = error as AxiosError<ApiError>;
      const errorType = errorTyped?.response?.data?.error?.type;
      if (errorType === EDiscoutCodeState.DiscountCodeNonApplicable) {
        const couponErrorData = {
          status: 'error' as Status,
          value: discountCode,
          helperText: 'Cupón invalido',
          buttonDisabled: true
        };

        if (mode === 'billing' && sendErrorCouponGtm) {
          sendErrorCouponGtm('discountCode');
        }

        if (mode === 'billing') {
          dispatch(setCouponData(couponErrorData));
        } else if (mode === 'buyNow') {
          dispatch(setBuyNowCouponData(couponErrorData));
        }
      }
      toast({
        title: 'No pudimos agregar el cupón',
        body: 'Por favor, intenta de nuevo.',
        position: 'bottom-left',
        status: 'error'
      });
      return rejectWithValue(errorTyped.response?.data || 'Error desconocido');
    }
  }
);

export const removeCartDiscount = createAsyncThunk(
  CART_REMOVE_DISCOUNT_CODE,
  async (discountCode: string) => {
    const cart = await removeDiscountCode(discountCode);

    toast({
      title: '¡Listo! Eliminaste un cupón ',
      body: 'Recuerda que podrás volver a ingresarlo.',
      position: 'bottom-left',
      status: 'success'
    });

    return cart && cart.data ? httpCartDtoToCart(cart.data) : undefined;
  }
);

export const getCart = createAsyncThunk<
  Cart,
  void,
  { rejectValue: TErrorResponse }
>(FETCH_CART, async (_, { rejectWithValue }) => {
  try {
    const response = await getActiveCart();
    const cart = httpCartDtoToCart(response.data);
    return cart;
  } catch (error) {
    const errorInfo = (error as AxiosError)?.response?.data as TErrorResponse;
    return rejectWithValue(errorInfo);
  }
});

export const updateLineItemQuantity = createAsyncThunk<
  Cart,
  {
    lineItemId: string;
    quantity: number;
    skusHistory?: shippingMethodBodyRequest[];
  },
  { state: RootState }
>(
  UPDATE_LINE_ITEM_QUANTITY,
  async (
    { lineItemId, quantity, skusHistory },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const cart = await updateLineItem(lineItemId, quantity);

      await dispatch(getShippingMethods({ params: skusHistory }));

      return httpCartDtoToCart(cart.data);
    } catch (error) {
      const errorInfo = (error as AxiosError)?.response?.data;
      return rejectWithValue(errorInfo);
    }
  }
);

export const removeLineItem = createAsyncThunk<
  Cart,
  string,
  { state: RootState }
>(REMOVE_LINE_ITEM, async (lineItemId: string, { rejectWithValue }) => {
  try {
    const cart = await removeLineItemFromCart(lineItemId);

    return httpCartDtoToCart(cart.data);
  } catch (error) {
    const errorInfo = (error as AxiosError)?.response?.data as TErrorResponse;
    return rejectWithValue(errorInfo);
  }
});
export const selectShippingMethod = createAsyncThunk(
  UPDATE_SHIPPING_METHOD,
  async (shippingMethod: HttpShippingMethodDraft[]) => {
    const cartWithShippingMethod =
      await selectCartShippingMethod(shippingMethod);
    return httpCartDtoToCart(cartWithShippingMethod.data);
  }
);

export const setOnBehalf = createAsyncThunk(
  SET_ON_BEHALF,
  async ({
    deliveryGroupId,
    authorizedPerson,
    isRegisteredUser = false
  }: {
    deliveryGroupId: string;
    authorizedPerson: AuthorizedPerson;
    isRegisteredUser?: boolean;
    customerDocumentInfo?: {
      documentType?: IdentificationDocumentType;
      documentID?: string;
      customerRut?: string;
    };
  }) => {
    const cart = await setOnBehalfOf(
      deliveryGroupId,
      authorizedPerson,
      isRegisteredUser
    );
    return httpCartDtoToCart(cart.data);
  }
);

export const addClubParis = createAsyncThunk(
  ADD_CLUB_PARIS,
  async (payload: ClubParisFields, { rejectWithValue }) => {
    try {
      const cart = await postClubParis(payload);
      toast({
        title: '¡Listo! Agregaste el código de beneficios.',
        position: 'top-right',
        status: 'success'
      });
      return httpCartDtoToCart(cart.data);
    } catch (error) {
      const errorInfo = (error as AxiosError)?.response?.data as TErrorResponse;
      toast({
        title: 'No pudimos agregar el código de beneficios',
        position: 'top-right',
        status: 'error'
      });
      return rejectWithValue(errorInfo);
    }
  }
);

export const removeClubParis = createAsyncThunk(
  REMOVE_CLUB_PARIS,
  async (
    sendSuccessfullyRemovedClubParisGtm: () => void,
    { rejectWithValue }
  ) => {
    try {
      const cart = await deleteClubParis();

      sendSuccessfullyRemovedClubParisGtm();
      toast({
        title: 'Eliminaste el código de beneficios.',
        position: 'top-right',
        status: 'success'
      });
      return httpCartDtoToCart(cart.data);
    } catch (error) {
      const errorInfo = (error as AxiosError)?.response?.data as TErrorResponse;
      toast({
        title: 'No pudimos eliminar el código de beneficios',
        position: 'top-right',
        status: 'error'
      });
      return rejectWithValue(errorInfo);
    }
  }
);

export const addInvoice = createAsyncThunk(
  ADD_INVOICE,
  async (payload: InvoiceFormFields, { rejectWithValue }) => {
    try {
      const cart = await postAddInvoice(payload);
      return httpCartDtoToCart(cart.data);
    } catch (error: unknown) {
      return rejectWithValue((error as AxiosError)?.response?.data);
    }
  }
);

export const removeInvoice = createAsyncThunk(REMOVE_INVOICE, async () => {
  const cart = await deleteInvoice();
  return httpCartDtoToCart(cart.data);
});

export const updateShippingOption = createAsyncThunk(
  UPDATE_SHIPPING_OPTION,
  async ({
    deliveryGroupId,
    shippingOptionDraft
  }: {
    deliveryGroupId: string;
    shippingOptionDraft: HttpShippingOptionDraft;
  }) => {
    const cart = await updateCartShippingOption(
      deliveryGroupId,
      shippingOptionDraft
    );
    return httpCartDtoToCart(cart.data);
  }
);

export const addToSaveForLater = createAsyncThunk(
  ADD_SAVE_FOR_LATER,
  async ({ sku, lineItemId }: { sku: string; lineItemId: string }) => {
    await addProductToSaveForLater(sku);
    const cart = await removeOutOfStockProduct(lineItemId);

    return httpCartDtoToCart(cart.data);
  }
);

export const removeCartOutOfStockItem = createAsyncThunk(
  REMOVE_OUT_OF_STOCK_ITEM,
  async ({ customLineItemId }: { customLineItemId: string }) => {
    const cart = await removeOutOfStockProduct(customLineItemId);
    return httpCartDtoToCart(cart.data);
  }
);

export const removeCartOutOfStockItems = createAsyncThunk(
  REMOVE_OUT_OF_STOCK_ITEMS,
  async () => {
    const cart = await removeOutOfStockProducts();
    return httpCartDtoToCart(cart.data);
  }
);

const CartReduxRepository: CartRepository = {
  saveShippingAddress: async (addressPayload) => {
    const { payload } = await store.dispatch(
      saveShippingAddress(addressPayload)
    );
    return payload as Cart;
  },
  saveShippingAddressForGuest: async (addressPayload) => {
    const { payload } = await store.dispatch(
      saveShippingAddressForGuest(addressPayload)
    );
    return payload as Cart;
  },
  saveShippingAddressCommune: async (addressPayload) => {
    const { payload } = await store.dispatch(
      saveShippingAddressCommune(addressPayload)
    );
    return payload as Cart;
  },
  saveBillingAddress: async (addressPayload) => {
    const { payload } = await store.dispatch(
      saveBillingAddress(addressPayload)
    );
    return payload as Cart;
  },
  addDiscountCode: async (discountCode: string, mode: 'billing' | 'buyNow') => {
    const { payload } = await store.dispatch(
      addCartDiscount({
        discountCode,
        mode,
        sendErrorCouponGtm: () => {},
        sendInvalidCouponGtm: () => {}
      })
    );
    return payload as Cart;
  },
  removeDiscountCode: async (discountCode) => {
    const { payload } = await store.dispatch(removeCartDiscount(discountCode));

    return payload as Cart;
  },
  get: async () => {
    const { payload } = await store.dispatch(getCart());
    return payload as Cart;
  },
  updateLineItemQuantity: async (lineItemId, quantity, skusHistory) => {
    const payload = await store
      .dispatch(updateLineItemQuantity({ lineItemId, quantity, skusHistory }))
      .unwrap();
    return payload as Cart;
  },
  removeLineItem: async (lineItemId) => {
    const { payload } = await store.dispatch(removeLineItem(lineItemId));
    return payload as Cart;
  },
  selectShippingMethod: async (shippingMethod) => {
    const { payload } = await store.dispatch(
      selectShippingMethod(shippingMethod)
    );

    return payload as Cart;
  },
  updateShippingOption: async (deliveryGroupId, shippingOptionDraft) => {
    const { payload } = await store.dispatch(
      updateShippingOption({ deliveryGroupId, shippingOptionDraft })
    );

    return payload as Cart;
  },
  addGiftCard: async (giftCard: GiftCardFormFields) => {
    const { payload } = await store.dispatch(addGiftCard(giftCard));
    return payload as Cart;
  },
  removeGiftCard: async () => {
    const { payload } = await store.dispatch(removeGiftCard());
    return payload as Cart;
  },
  addToSaveForLater: async (sku: string, lineItemId: string) => {
    const { payload } = await store.dispatch(
      addToSaveForLater({ sku, lineItemId })
    );
    return payload as Cart;
  },
  removeOutOfStockItem: async (customLineItemId: string) => {
    const { payload } = await store.dispatch(
      removeCartOutOfStockItem({ customLineItemId })
    );
    return payload as Cart;
  },
  removeOutOfStockItems: async () => {
    const { payload } = await store.dispatch(removeCartOutOfStockItems());
    return payload as Cart;
  },
  mergeCart: async (anonymousId: string) => {
    const { payload } = await store.dispatch(mergeCart(anonymousId));
    return payload as Cart;
  },
  setOnBehalf: async (
    deliveryGroupId,
    authorizedPerson,
    customerDocumentInfo,
    isRegisteredUser = false
  ) => {
    const { payload } = await store.dispatch(
      setOnBehalf({
        deliveryGroupId,
        authorizedPerson,
        customerDocumentInfo,
        isRegisteredUser
      })
    );
    return payload as Cart;
  },
  addInvoice: async (invoice: InvoiceFormFields) => {
    const { payload } = await store.dispatch(addInvoice(invoice));
    return payload as Cart;
  },
  removeInvoice: async () => {
    const { payload } = await store.dispatch(removeInvoice());
    return payload as Cart;
  }
};

export default CartReduxRepository;
